import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../../../cache";
import { emailAuthApi } from "../api/emailAuthApi";
import { AuthLayout, AuthField, AuthError, AuthNotice, CodeInput } from "../components/AuthLayout";
import { APP_TITLE, BRAND } from "../../../branding";

// mode: "choose" | "email" | "totp" | "forgot" | "forgot-sent"
export default function LoginPage({ initialMode = "choose" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Set by the API's Microsoft callback when an Email-method user tries Microsoft.
  const loginError = params.get("login_error");
  const [mode, setMode] = useState(loginError === "use_email" ? "email" : initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const go = (next) => {
    setError("");
    setCode("");
    if (next === "choose") setPassword("");
    setMode(next);
    if (next === "choose" && initialMode !== "choose") navigate("/", { replace: true });
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e) => {
    e.preventDefault();
    run(async () => {
      await emailAuthApi.login(email.trim(), password);
      setPassword("");
      setMode("totp");
    });
  };

  const submitCode = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        await emailAuthApi.verify(code);
      } catch (err) {
        setCode("");
        // Pending sign-in expired: back to the password step.
        if (err.status === 401 && /timed out/i.test(err.message)) setMode("email");
        throw err;
      }
      // Full reload so App re-reads /me with the new session cookie.
      window.location.assign("/");
    });
  };

  const submitForgot = (e) => {
    e.preventDefault();
    run(async () => {
      await emailAuthApi.forgotPassword(email.trim());
      setMode("forgot-sent");
    });
  };

  if (mode === "email") {
    return (
      <AuthLayout eyebrow="Sign in with email" title="Welcome back" subtitle="For invited partners and customers using email sign-in.">
        {loginError === "use_email" && (
          <AuthNotice>Your account is set up for <b>email sign-in</b>, not Microsoft. Sign in with your email, password and authenticator code.</AuthNotice>
        )}
        <form className="auth-form" onSubmit={submitPassword}>
          <AuthField label="Email">
            <input className="auth-input" type="email" autoComplete="username" autoFocus required
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </AuthField>
          <AuthField label="Password">
            <input className="auth-input" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </AuthField>
          <AuthError message={error} />
          <button className="auth-btn auth-btn--primary" type="submit" disabled={busy || !email || !password}>
            {busy ? "Checking…" : "Continue"}
          </button>
          <div className="auth-links">
            <button type="button" className="auth-link" onClick={() => go("forgot")}>Forgot password?</button>
            <button type="button" className="auth-link" onClick={() => go("choose")}>← Other sign-in options</button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  if (mode === "totp") {
    return (
      <AuthLayout eyebrow="Two-factor authentication" title="Enter your code"
        subtitle={`Open your authenticator app and enter the 6-digit code for ${APP_TITLE}.`}>
        <form className="auth-form" onSubmit={submitCode}>
          <CodeInput value={code} onChange={setCode} />
          <AuthError message={error} />
          <button className="auth-btn auth-btn--primary" type="submit" disabled={busy || code.length !== 6}>
            {busy ? "Verifying…" : "Verify and sign in"}
          </button>
          <div className="auth-links">
            <button type="button" className="auth-link" onClick={() => go("email")}>← Back</button>
          </div>
          <p className="auth-fineprint">Lost access to your authenticator app? Ask your ProTrack administrator to reset your two-factor authentication.</p>
        </form>
      </AuthLayout>
    );
  }

  if (mode === "forgot") {
    return (
      <AuthLayout eyebrow="Password reset" title="Forgot your password?"
        subtitle="We'll email you a reset link. You'll also need the current code from your authenticator app.">
        <form className="auth-form" onSubmit={submitForgot}>
          <AuthField label="Email">
            <input className="auth-input" type="email" autoComplete="username" autoFocus required
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </AuthField>
          <AuthError message={error} />
          <button className="auth-btn auth-btn--primary" type="submit" disabled={busy || !email}>
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <div className="auth-links">
            <button type="button" className="auth-link" onClick={() => go("email")}>← Back to sign in</button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  if (mode === "forgot-sent") {
    return (
      <AuthLayout eyebrow="Password reset" title="Check your inbox">
        <AuthNotice>
          If <b>{email}</b> is registered for email sign-in, a reset link is on its way. It expires in 30 minutes.
        </AuthNotice>
        <div className="auth-links">
          <button type="button" className="auth-link" onClick={() => go("email")}>← Back to sign in</button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow={APP_TITLE} title="Welcome back" subtitle="Sign in to continue.">
      <div className="auth-form">
        <button className="auth-btn auth-btn--primary" type="button" onClick={() => { window.location.href = `${API}/login`; }}>
          <MicrosoftIcon /> Sign in with Microsoft
        </button>
        <div className="auth-divider"><span>or</span></div>
        <button className="auth-btn auth-btn--secondary" type="button" onClick={() => go("email")}>
          Sign in with Email
        </button>
        <p className="auth-fineprint">Email sign-in is only available to invited external users. {BRAND.orgName ? `${BRAND.orgName} staff` : "Internal staff"} should use Microsoft.</p>
      </div>
    </AuthLayout>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
